import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AppIcon, { AppIconType } from "../AppIcon/AppIcon";
import AppText, { FormLabel } from "../AppText/AppText";
import { appTheme } from "../../../theme/Theme";

export interface TextAreaNewProps {
	value?: string;
	onChange?: any;
	onChangeText?: (text: string) => void;
	name?: string;
	errors?: any;
	label?: string | string[];
	icon?: AppIconType;
	disabled?: boolean;
	placeholder?: string;
	className?: string;
	field?: string;
	containerRef?: any;
	onFocus?: any;
	onBlur?: any;
	readOnly?: boolean;
	defaultValue?: string;
	description?: string;
	/**
	 * Will shows asterisk next to label which indicates that field
	 * is mandatory. Will not add any validation logic!
	 */
	mandatory?: boolean;

	/**
	 * Optional tooltip ℹ️
	 */
	tooltip?: string | React.ReactNode;

	/**
	 * Optional knowledge base tag to open knowledge base sidebar
	 */
	knowledgeBaseTag?: string;

	/**
	 * Optional custom tooltip text for knowledge base trigger
	 */
	knowledgeBaseTooltip?: string;

	/**
	 * Optional knowledge base tag that only shows on hover (reduces visual clutter)
	 */
	showKnowledgeBaseForTagOnHover?: string;

	/**
	 * If to skip attempt to translate label.
	 * Should in most cases not be needed.
	 */
	skipLabelTranslation?: boolean;

	/**
	 * Height of the textarea
	 */
	height?: string;

	/**
	 * Width of the textarea
	 */
	width?: string;

	/**
	 * Number of rows for the textarea
	 */
	rows?: number;

	/**
	 * Number of columns for the textarea
	 */
	cols?: number;

	/**
	 * Whether the textarea can be resized
	 */
	resize?: "none" | "both" | "horizontal" | "vertical";

	isValid?: boolean;

	/**
	 * Custom className for the textarea container
	 */
	containerClassName?: string;

	/**
	 * Custom className for the textarea wrapper (the styled border container)
	 */
	wrapperClassName?: string;

	/**
	 * Custom className for the textarea element itself
	 */
	textareaClassName?: string;

	/**
	 * When enabled, the textarea renders collapsed at `rows` height and, while
	 * focused, grows to fit its content up to `maxRows` (scrolls beyond that).
	 * On blur it collapses back to `rows`.
	 */
	autoGrowOnFocus?: boolean;

	/**
	 * Max number of rows the textarea grows to while focused (only used together
	 * with `autoGrowOnFocus`). Content beyond this scrolls. Defaults to 20.
	 */
	maxRows?: number;

	/**
	 * AI Generate button configuration
	 */
	aiGenerateButton?: {
		onClickAiGenerateButton?: () => void;
		aiGenerateButtonText?: string;
		disabledAiGenerateButton?: boolean;
		loadingAiGenerateButton?: boolean;
		tooltipText?: string;
		loadingText?: string;
	};
}

const TextAreaNew = React.forwardRef<HTMLTextAreaElement, TextAreaNewProps>(
	(
		{
			name,
			value,
			onChange,
			onChangeText,
			errors,
			label,
			icon,
			disabled,
			placeholder,
			className,
			field,
			containerRef,
			onFocus,
			onBlur,
			readOnly,
			defaultValue,
			description,
			mandatory,
			tooltip,
			knowledgeBaseTag,
			knowledgeBaseTooltip,
			showKnowledgeBaseForTagOnHover,
			skipLabelTranslation = false,
			height,
			width,
			rows = 4,
			cols,
			resize = "vertical",
			isValid = true,
			containerClassName,
			wrapperClassName,
			textareaClassName,
			autoGrowOnFocus,
			maxRows = 20,
			aiGenerateButton,
		},
		ref
	) => {
		const { t } = useTranslation();
		const localRef = useRef<HTMLTextAreaElement | null>(null);
		const [isFocused, setIsFocused] = useState(false);

		// Merge the forwarded ref (e.g. react-hook-form's `register` ref) with a
		// local ref so we can measure/resize the textarea imperatively.
		const setRefs = useCallback(
			(node: HTMLTextAreaElement | null) => {
				localRef.current = node;
				if (typeof ref === "function") {
					ref(node);
				} else if (ref) {
					(ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
				}
			},
			[ref]
		);

		// Matches the textarea's `leading-[1.25rem]` line-height (20px).
		const LINE_HEIGHT = 20;

		const adjustHeight = useCallback(() => {
			const el = localRef.current;
			if (!el || !autoGrowOnFocus) return;

			const collapsedHeight = rows * LINE_HEIGHT;

			if (!isFocused) {
				el.style.height = `${collapsedHeight}px`;
				el.style.overflowY = "hidden";
				return;
			}

			const maxHeight = maxRows * LINE_HEIGHT;
			el.style.height = "auto";
			const contentHeight = el.scrollHeight;
			const nextHeight = Math.max(collapsedHeight, Math.min(contentHeight, maxHeight));
			el.style.height = `${nextHeight}px`;
			el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
		}, [autoGrowOnFocus, isFocused, rows, maxRows]);

		useLayoutEffect(() => {
			adjustHeight();
		}, [adjustHeight, value]);

		// Base classes for different parts
		const containerClasses = `w-full ${containerClassName || ""}`;

		const headerClasses = `
			flex justify-between items-center
		`
			.trim()
			.replace(/\s+/g, " ");

		const wrapperClasses = `
			flex flex-col max-w-full p-3 rounded-sm bg-white border border-gray-300
			focus-within:border-blue-900 focus-within:bg-white
			${!isValid ? "border-red-500 bg-red-50" : ""}
			${disabled ? "bg-gray-100 cursor-not-allowed" : ""}
			${wrapperClassName || ""}
		`
			.trim()
			.replace(/\s+/g, " ");

		const textareaClasses = `
			border-none w-full text-[0.9375rem] leading-[1.25rem] bg-transparent outline-none resize-${
				autoGrowOnFocus ? "none" : resize
			}
			${height ? "" : "min-h-[75px]"}
			${disabled ? "cursor-not-allowed text-gray-500" : ""}
			${textareaClassName || ""}
		`
			.trim()
			.replace(/\s+/g, " ");

		const aiButtonClasses = `
			flex items-center mb-[2px] pr-0.5 py-1 text-sm text-blue-500 bg-transparent border-none cursor-pointer
			hover:bg-blue-50 rounded transition-colors duration-200 
			${aiGenerateButton?.disabledAiGenerateButton ? "opacity-50 cursor-not-allowed" : ""}
		`;

		return (
			<div className={containerClasses} ref={containerRef}>
				<div className={headerClasses}>
					<div className="flex items-center">
						{label && (
							<FormLabel
								tooltip={tooltip}
								knowledgeBaseTag={knowledgeBaseTag}
								knowledgeBaseTooltip={knowledgeBaseTooltip}
								showKnowledgeBaseForTagOnHover={showKnowledgeBaseForTagOnHover}
							>
								{skipLabelTranslation ? label : t(label as any)}
								{mandatory ? " *" : ""}
							</FormLabel>
						)}
					</div>
					<div className="flex items-center space-x-2">
						{icon && <AppIcon icon={icon} color="#999" />}
						{aiGenerateButton && (
							<div className="relative group">
								<button
									type="button"
									className={aiButtonClasses}
									onClick={aiGenerateButton.onClickAiGenerateButton}
									disabled={aiGenerateButton.disabledAiGenerateButton}
									title={aiGenerateButton.tooltipText}
								>
									<div className="mr-0.5">
										<AppIcon icon="ai" size="16px" color="#3B82F6" />
									</div>
									{aiGenerateButton.loadingAiGenerateButton
										? aiGenerateButton.loadingText || "Loading..."
										: t(aiGenerateButton.aiGenerateButtonText as any)}
								</button>
								{aiGenerateButton.tooltipText && aiGenerateButton.disabledAiGenerateButton && (
									<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
										{aiGenerateButton.tooltipText}
									</div>
								)}
							</div>
						)}
					</div>
				</div>
				<div className={wrapperClasses}>
					<textarea
						className={textareaClasses}
						name={name}
						onChange={(e) => {
							if (onChangeText) {
								onChangeText(e.target.value);
							} else if (onChange) {
								onChange(e);
							}
							adjustHeight();
						}}
						onFocus={(e) => {
							setIsFocused(true);
							onFocus?.(e);
						}}
						onBlur={(e) => {
							setIsFocused(false);
							onBlur?.(e);
						}}
						value={value}
						ref={setRefs}
						placeholder={placeholder && t(placeholder as any)}
						disabled={disabled}
						readOnly={readOnly}
						defaultValue={defaultValue}
						rows={rows}
						cols={cols}
						style={{
							height: height,
							width: width,
						}}
					/>
				</div>
				{description && (
					<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="grayText">
						{t(description as any)}
					</AppText>
				)}
				<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="danger">
					{errors && t(errors as any)}
				</AppText>
			</div>
		);
	}
);

TextAreaNew.displayName = "TextAreaNew";

export default TextAreaNew;
